import { type ColumnDef } from '@tanstack/react-table';

import {
  DataTable,
  DataTableColumnsMenu,
  DataTableInteractiveArea,
} from '@spotify-internal/data-table';
import { ChipFilter, Flex } from '@spotify-internal/encore-web';
import { ListEmptyState, SearchField } from '@spotify/confidence-core-react';
import {
  DataTableInfiniteScroll,
  ResourceDataTableContainer,
  useResourceDataTable,
} from '@spotify/confidence-resource-data-table-react';

import type { CollectionEntryTableRow } from './collectionEntryColumns';

import styles from './CollectionEntriesTable.module.css';

type CollectionEntriesTableProps = {
  columns: ColumnDef<CollectionEntryTableRow, CollectionEntryTableRow>[];
  rows: CollectionEntryTableRow[];
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (
    updater:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  scrollRef: React.RefObject<HTMLElement>;
  scrollElement: HTMLElement | null;
  isLoading: boolean;
  hasEntries: boolean;
  hasNextPage: boolean;
  hasActiveFilters: boolean | string;
  error: Error | undefined;
  onLoadMore: () => Promise<unknown>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  typeFilter: string;
  availableTypes: { id: string; label: string }[];
  onTypeSelect: (typeId: string) => void;
};

export const CollectionEntriesTable = ({
  columns,
  rows,
  columnVisibility,
  onColumnVisibilityChange,
  scrollRef,
  scrollElement,
  isLoading,
  hasEntries,
  hasNextPage,
  hasActiveFilters,
  error,
  onLoadMore,
  searchQuery,
  onSearchChange,
  typeFilter,
  availableTypes,
  onTypeSelect,
}: CollectionEntriesTableProps) => {
  const { table, interactionProps } =
    useResourceDataTable<CollectionEntryTableRow>({
      columns,
      data: rows,
      columnVisibility,
      onColumnVisibilityChange,
      externalScrollRef: scrollRef,
    });

  return (
    <>
      <Flex className={styles.searchBar} gap="tighter-2" alignItems="center">
        <Flex
          className={styles.searchBarPrimary}
          gap="tighter"
          alignItems="center"
        >
          <SearchField
            className={styles.searchField}
            placeholder="Search entries"
            aria-label="Search entries"
            value={searchQuery}
            onChange={e => onSearchChange(e.currentTarget.value)}
           data-track-source-anchor="82d5dbdeeb"/>
          {availableTypes.length > 1 && (
            <Flex gap="tighter-2">
              <ChipFilter
                isSelected={typeFilter === 'all'}
                onClick={() => onTypeSelect('all')}
               data-track-source-anchor="c75012ef08">
                All
              </ChipFilter>
              {availableTypes.map(type => (
                <ChipFilter
                  key={type.id}
                  isSelected={typeFilter === type.id}
                  onClick={() => onTypeSelect(type.id)}
                 data-track-source-anchor="77b577b263;collection-entries-table-chip-filter">
                  {type.label}
                </ChipFilter>
              ))}
            </Flex>
          )}
        </Flex>
        <DataTableColumnsMenu instance={table} />
      </Flex>
      <DataTableInteractiveArea {...interactionProps}>
        <ResourceDataTableContainer>
          <DataTable
            instance={table}
            aria-label="Collection entries"
            isLoading={isLoading && !hasEntries}
            emptyState={
              hasActiveFilters && !hasNextPage && !isLoading && !error ? (
                <ListEmptyState
                  title="No matching entries"
                  description="Try a different search or filter."
                />
              ) : undefined
            }
          />
        </ResourceDataTableContainer>
      </DataTableInteractiveArea>
      <DataTableInfiniteScroll
        root={scrollElement}
        itemCount={rows.length}
        hasNextPage={hasNextPage}
        loading={isLoading && hasEntries}
        onLoadMore={onLoadMore}
      >
        Loading more entries…
      </DataTableInfiniteScroll>
    </>
  );
};
